import mitt from "mitt";
import { computed, makeAutoObservable, toJS } from "mobx";

export enum ViewerIds {
  Viewer1 = "viewer1",
  Viewer2 = "viewer2",
}

export type ViewerId = ViewerIds.Viewer1 | ViewerIds.Viewer2;

export interface Selection {
  viewerId: ViewerId;
  urns: string[];
}

export type SelectionMode = "SingleModel" | "SingleScenarioModel" | "SingleScenario" | "DualScenarios";

export enum SelectionManagerEvents {
  SELECTION_ONLOAD_EVENT = "onLoad",
  SELECTION_ONUNLOAD_EVENT = "onUnLoad",
  SELECTION_MODE_CHANGED_EVENT = "selectionModeChanged",
  SELECTION_CHANGED_EVENT = "selectionChanged",
}

export class SelectionManager {
  selectionMode: SelectionMode = "SingleScenarioModel";
  selections: Record<string, Selection> = {};
  emitter: any = mitt(); // f-ts

  constructor() {
    makeAutoObservable(
      this,
      {
        selectCount: computed,
      },
      { autoBind: true },
    );
  }

  get dump() {
    return toJS(this);
  }

  get scenarioIds(): string[] {
    return Object.keys(this.selections);
  }

  get selectCount(): number {
    return Object.keys(this.selections).length;
  }

  get maxScenarioSelections(): number {
    switch (this.selectionMode) {
      case "SingleModel":
        return 1;
      case "SingleScenarioModel":
        return 1;
      case "SingleScenario":
        return 1;
      case "DualScenarios":
        return 2;
      default:
        return 1;
    }
  }

  // TODO: change these to use mitt events
  onLoad: (scenarioId: string, viewerId: ViewerId, urns: string | string[]) => Promise<void> = () => {
    return new Promise<void>((resolve) => resolve());
  };

  // TODO: change these to use mitt events
  onUnload: (scenarioId: string, viewerId: ViewerId, urns: string | string[]) => Promise<void> = () => {
    return new Promise<void>((resolve) => resolve());
  };

  getSelections(index: number): Selection {
    const scenarioId: string = this.scenarioIds[index];
    return this.selections[scenarioId];
  }

  getScenarioIdByViewerId(viewerId: ViewerId): string | undefined {
    return Object.keys(this.selections).find((key: string) => this.selections[key].viewerId === viewerId);
  }

  /*
  * Converts a viewerIndex [0, 1] to a viewerId [Viewer1, Viewer2]
  * then does a lookup to find the scenarioId
  */
  getScenarioIdByViewerIndex(viewerIndex: number): string | undefined {
    const viewerId = viewerIndex === 0 ? ViewerIds.Viewer1 : ViewerIds.Viewer2;
    return Object.keys(this.selections).find((key: string) => this.selections[key].viewerId === viewerId);
  }

  getViewerIdByScenarioId(scenarioId: string): ViewerId | undefined {
    return this.selections[scenarioId]?.viewerId;
  }

  getSelectedScenarioId(): string | undefined {
    if (Object.keys(this.selections).length > 0) {
      return Object.keys(this.selections)[0];
    } else {
      return undefined;
    }
  }

  getSelectedModelUrns(scenarioId: string): string[] {
    const selection = this.selections[scenarioId];
    return selection?.urns || [];
  }

  setMode(mode: SelectionMode): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        console.log("SelectionManager.setMode: ", mode);
        if (mode !== this.selectionMode) {
          await this.unSelectAllScenarios();
          this.selectionMode = mode;
        }
        resolve();
      })();
    });
  }

  selectScenario(scenarioId: string, urns?: string | string[], emit: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        if (!Array.isArray(urns)) {
          urns = urns ? [urns] : [];
        }

        if (this.selectionMode === "SingleModel" || this.selectionMode === "SingleScenarioModel" || this.selectionMode === "SingleScenario") {
          const hasScenario = Object.hasOwnProperty.call(this.selections, scenarioId);
          if (!hasScenario) {
            await this.unSelectAllScenarios();
          }

          this.selections[scenarioId] = {
            viewerId: ViewerIds.Viewer1,
            urns,
          };

          if (urns.length > 0) {
            await this.onLoad(scenarioId, ViewerIds.Viewer1, urns);
          }
        }
        if (this.selectionMode === "DualScenarios") {
          if (Object.hasOwnProperty.call(this.selections, scenarioId)) {
            const selection = this.selections[scenarioId];
            selection.urns = urns.concat(selection.urns.filter((urn) => !urns?.includes(urn)));
            //if (Object.keys(this.selections).length === 1) {
            //  selection.viewerId = ViewerIds.Viewer1;
            //}
            if (selection.urns.length > 0) {
              await this.onLoad(scenarioId, selection.viewerId, selection.urns);
            }
          } else {
            // Determine the viewer to target
            let selection;
            try {
              selection = this.selections[Object.keys(this.selections)[0]];
            } catch {
              /*nada*/
            }

            let viewerId: ViewerId = ViewerIds.Viewer1;
            if (selection) {
              viewerId = selection.viewerId === ViewerIds.Viewer1 ? ViewerIds.Viewer2 : ViewerIds.Viewer1;
            }

            this.selections[scenarioId] = {
              viewerId,
              urns,
            };

            if (urns?.length > 0) {
              await this.onLoad(scenarioId, viewerId, urns);
            }
          }
        }

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT, {
            type: SelectionManagerEvents.SELECTION_CHANGED_EVENT,
          });
        }

        //console.log('SelectionManager.selections: ', toJS(this.selections));
        resolve();
      })();
    });
  }

  unSelectScenario(scenarioId: string, emit: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        const selection = this.selections[scenarioId];
        if (selection) {
          await this.onUnload(scenarioId, selection.viewerId, selection.urns);
          delete this.selections[scenarioId];

          if (emit) {
            this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT, {
              type: SelectionManagerEvents.SELECTION_CHANGED_EVENT,
            });
          }
        }

        //console.log('SelectionManager.selections: ', toJS(this.selections));
        resolve();
      })();
    });
  }

  unSelectAllScenarios(emit: boolean = false): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        // Object.keys(this.selections).map(async (key) => {
        //   await this.unSelectScenario(key, false);
        // });

        await Promise.all(Object.keys(this.selections).map(async (key) => {
          await this.unSelectScenario(key, false);
        }));

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT, {
            type: SelectionManagerEvents.SELECTION_CHANGED_EVENT,
          });
        }

        console.log("SelectionManager.selections: ", toJS(this.selections));
        resolve();
      })();
    });
  }

  selectModel(urn: string, emit: boolean = false): Promise<void> {
    console.log("SelectionManager.selectModel: ", urn);
    return new Promise<void>((resolve) => {
      (async () => {
        if (this.selectionMode === "SingleModel") {
          await this.unSelectAllScenarios(false);
          await this.selectScenario("SingleModel", urn, false);

          if (emit) {
            this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT, {
              type: SelectionManagerEvents.SELECTION_CHANGED_EVENT,
            });
          }
        }

        resolve();
      })();
    });
  }

  selectModels(scenarioId: string, urns: string | string[], emit: boolean = false, loadInViewer: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        if (!Array.isArray(urns)) {
          urns = urns ? [urns] : [];
        }

        if (Object.hasOwnProperty.call(this.selections, scenarioId)) {
          const selection = this.selections[scenarioId];
          if (this.selectionMode === "SingleScenarioModel") {
            const unloadUrns = selection.urns.filter((urn: string) => !urns.includes(urn));
            await this.onUnload(scenarioId, selection.viewerId, unloadUrns);
            this.selections[scenarioId].urns = [];
          }

          selection.urns = urns.concat(selection.urns.filter((urn) => !urns.includes(urn)));

          if (loadInViewer && urns?.length > 0) {
            await this.onLoad(scenarioId, selection.viewerId, urns);
          }

          console.log("SelectionManager.selections: ", toJS(this.selections));
        } else {
          await this.selectScenario(scenarioId, urns, false);
        }

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT, {
            type: SelectionManagerEvents.SELECTION_CHANGED_EVENT,
          });
        }

        resolve();
      })();
    });
  }

  unSelectModels(scenarioId: string, urns: string | string[], emit: boolean = false, loadInViewer: boolean = true): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        if (Object.hasOwnProperty.call(this.selections, scenarioId)) {
          const selection = this.selections[scenarioId];

          selection.urns = selection.urns.filter((urn) => !urns.includes(urn));

          if (loadInViewer && urns?.length > 0) {
            await this.onUnload(scenarioId, selection.viewerId, urns);
          }
        }

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT, {
            type: SelectionManagerEvents.SELECTION_CHANGED_EVENT,
          });
        }

        //console.log('SelectionManager.selections: ', toJS(this.selections));
        resolve();
      })();
    });
  }

  unSelectAllModels(scenarioId: string, emit: boolean = false): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        if (Object.hasOwnProperty.call(this.selections, scenarioId)) {
          const selection = this.selections[scenarioId];

          const urns = selection.urns;

          if (urns.length > 0) {
            await this.onUnload(scenarioId, selection.viewerId, urns);
            selection.urns = [];
          }
        }

        if (emit) {
          this.emitter.emit(SelectionManagerEvents.SELECTION_CHANGED_EVENT, {
            type: SelectionManagerEvents.SELECTION_CHANGED_EVENT,
          });
        }

        //console.log('SelectionManager.selections: ', toJS(this.selections));
        resolve();
      })();
    });
  }

  refreshViews(): Promise<void> {
    return new Promise<void>((resolve) => {
      (async () => {
        console.log("SelectionManager.refreshViews: ", toJS(this.selections));
        // Object.keys(this.selections).map(async (key) => {
        //   const selection = this.selections[key];
        //   await this.onLoad(key, selection.viewerId, selection.urns);
        // });
        
        await Promise.all(Object.keys(this.selections).map(async (key) => {
          const selection = this.selections[key];
          await this.onLoad(key, selection.viewerId, selection.urns);
        }));

        resolve();
      })();
    });
  }
}
